import { useState } from 'react';
import { Download, FileSpreadsheet, FileText, X, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { exportAttendanceExcel } from '@/services/adminAttendanceService';
import type { AdminAttendanceFilters } from '@/types/admin-attendance.types';

type ExportFormat = 'excel' | 'pdf';

interface AttendanceExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  filters: AdminAttendanceFilters;
  filterSummary: {
    campusNames: string;
    academicYearName: string;
    semesterLabels: string;
    filiereNames: string;
    niveauLabels: string;
  };
}

export function AttendanceExportModal({ isOpen, onClose, filters, filterSummary }: AttendanceExportModalProps) {
  const [format, setFormat] = useState<ExportFormat>('excel');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  if (!isOpen) return null;

  const handleExport = async () => {
    setStatus('loading');
    setErrorMsg('');
    try {
      if (format === 'excel') {
        await exportAttendanceExcel(filters);
      } else {
        await exportAttendanceExcel(filters);
      }
      setStatus('success');
      setTimeout(() => {
        setStatus('idle');
        onClose();
      }, 1500);
    } catch (err: any) {
      setStatus('error');
      setErrorMsg(err?.message || "Erreur lors de l'export");
    }
  };

  const handleClose = () => {
    if (status === 'loading') return;
    setStatus('idle');
    setErrorMsg('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4" onClick={handleClose}>
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
        style={{ animation: 'modalPop 0.3s ease-out' }}
        onClick={(e) => e.stopPropagation()}
      >
        <style>{`
          @keyframes modalPop { 0% { transform: scale(0.9); opacity: 0; } 50% { transform: scale(1.02); } 100% { transform: scale(1); opacity: 1; } }
          @keyframes iconBounce { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.1); } }
        `}</style>

        {/* Close button */}
        <div className="flex justify-end p-3 pb-0">
          <button onClick={handleClose} className="p-1.5 rounded-full hover:bg-gray-100 transition-colors" disabled={status === 'loading'}>
            <X size={18} className="text-gray-400" />
          </button>
        </div>

        {/* Icon */}
        <div className="flex justify-center -mt-1">
          <div
            className="p-5 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 ring-8 ring-blue-50"
            style={{ animation: 'iconBounce 0.5s ease-out' }}
          >
            {status === 'success' ? (
              <CheckCircle size={36} className="text-green-500" strokeWidth={2} />
            ) : status === 'error' ? (
              <AlertCircle size={36} className="text-red-500" strokeWidth={2} />
            ) : (
              <Download size={36} className="text-blue-500" strokeWidth={2} />
            )}
          </div>
        </div>

        {/* Title */}
        <div className="text-center mt-4 px-6">
          <h3 className="text-lg font-bold text-gray-900">
            {status === 'success' ? 'Export termine !' : status === 'error' ? 'Erreur' : 'Exporter les Donnees'}
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            {status === 'success'
              ? 'Le fichier a ete telecharge avec succes.'
              : status === 'error'
              ? errorMsg
              : "Telechargez le rapport d'assiduite"}
          </p>
        </div>

        {status === 'idle' || status === 'loading' ? (
          <>
            {/* Format selection */}
            <div className="px-6 mt-5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Format d'export</p>
              <div className="grid grid-cols-1 gap-3">
                <button
                  onClick={() => setFormat('excel')}
                  disabled={status === 'loading'}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    format === 'excel'
                      ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <FileSpreadsheet size={24} className={format === 'excel' ? 'text-blue-600' : 'text-gray-400'} />
                  <div className="mt-2 font-semibold text-sm text-gray-900">Excel</div>
                  <div className="text-xs text-gray-500">Fichier .xlsx</div>
                </button>
              </div>
            </div>

            {/* Applied filters summary */}
            <div className="px-6 mt-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Filtres appliques</p>
              <div className="bg-gray-50 rounded-xl p-3 text-xs text-gray-600 space-y-1">
                <div><span className="font-medium">Campus:</span> {filterSummary.campusNames}</div>
                <div><span className="font-medium">Annee:</span> {filterSummary.academicYearName}</div>
                <div><span className="font-medium">Semestres:</span> {filterSummary.semesterLabels}</div>
                <div><span className="font-medium">Filieres:</span> {filterSummary.filiereNames}</div>
                <div><span className="font-medium">Niveaux:</span> {filterSummary.niveauLabels}</div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 px-6 py-5 mt-2">
              <button
                onClick={handleClose}
                disabled={status === 'loading'}
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleExport}
                disabled={status === 'loading'}
                className="flex-1 px-4 py-2.5 rounded-xl bg-blue-600 text-sm font-medium text-white hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {status === 'loading' ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Export en cours...
                  </>
                ) : (
                  <>
                    <Download size={16} />
                    Telecharger
                  </>
                )}
              </button>
            </div>
          </>
        ) : (
          /* Success / Error close button */
          <div className="flex justify-center px-6 py-5 mt-2">
            <button
              onClick={handleClose}
              className="px-8 py-2.5 rounded-xl bg-gray-100 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors"
            >
              Fermer
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
